import { HUD, HUD_THEME } from '../config/tuning';

/**
 * HUD metrics and drawing primitives (SPEC §3.4).
 *
 * Thin lines, one colour, no fills, no shadows, no rounded panels. Everything is quoted in 1080p
 * pixels in config and scaled here, so the instrument keeps its proportions at any resolution.
 *
 * The painter also owns the two things that would otherwise be repeated in every element: the
 * outline pass that makes high-contrast mode work, and a small text cache. The cache exists
 * because the HUD redraws every frame inside the render loop, and `value.toFixed(0)` allocates a
 * string each time — 900 short-lived strings a second is not a leak, but it is exactly the kind
 * of avoidable garbage SPEC §4.3 asks this project not to produce.
 */

export type HudTheme = (typeof HUD_THEME)[keyof typeof HUD_THEME];

/** Named cache slots, one per numeric readout that redraws every frame. */
export const TextSlot = {
  Airspeed: 0,
  Altitude: 1,
  LoadFactor: 2,
  Heading: 3,
  Endurance: 4,
  Chaff: 5,
  Flares: 6,
  LinkQuality: 7,
  LadderLabel: 8,
  TapeLabel: 9,
  Exposure: 10,
  Throttle: 11,
  ControlLock: 12,
  Count: 13,
} as const;

interface TextSlotState {
  value: number;
  decimals: number;
  text: string;
}

export class HudLayout {
  widthPx = 1;
  heightPx = 1;
  centreX = 0.5;
  centreY = 0.5;
  /** Resolution scale relative to the 1080p reference. */
  scale = 1;
  lineWidthPx: number = HUD.lineWidth;
  fontPx: number = HUD.fontPx;
  /** Angular scale from the camera; set each frame. */
  pixelsPerRadian = 1;

  private theme: HudTheme = HUD_THEME.standard;

  resize(widthPx: number, heightPx: number): void {
    this.widthPx = Math.max(1, widthPx);
    this.heightPx = Math.max(1, heightPx);
    this.centreX = this.widthPx * 0.5;
    this.centreY = this.heightPx * 0.5;
    this.scale = this.heightPx / 1080;
    this.applyTheme(this.theme);
  }

  applyTheme(theme: HudTheme): void {
    this.theme = theme;
    this.lineWidthPx = HUD.lineWidth * this.scale * theme.lineWidthScale;
    this.fontPx = HUD.fontPx * this.scale * theme.fontScale;
  }

  /** Scale a length quoted in 1080p pixels. */
  px(value1080: number): number {
    return value1080 * this.scale;
  }
}

export class HudPainter {
  readonly layout = new HudLayout();

  private context: CanvasRenderingContext2D;
  private theme: HudTheme = HUD_THEME.standard;
  private readonly slots: TextSlotState[] = [];
  private letterSpacingSupported = false;

  constructor(context: CanvasRenderingContext2D) {
    this.context = context;
    for (let i = 0; i < TextSlot.Count; i += 1) {
      this.slots.push({ value: Number.NaN, decimals: -1, text: '' });
    }
    // `letterSpacing` is a recent addition; where it is missing the text is drawn per character.
    this.letterSpacingSupported = 'letterSpacing' in context;
  }

  get ctx(): CanvasRenderingContext2D {
    return this.context;
  }

  setContext(context: CanvasRenderingContext2D): void {
    this.context = context;
    this.letterSpacingSupported = 'letterSpacing' in context;
  }

  setTheme(theme: HudTheme): void {
    this.theme = theme;
    this.layout.applyTheme(theme);
  }

  get currentTheme(): HudTheme {
    return this.theme;
  }

  /** Reset per-frame canvas state. */
  begin(): void {
    const ctx = this.context;
    ctx.clearRect(0, 0, this.layout.widthPx, this.layout.heightPx);
    ctx.globalAlpha = this.theme.opacity;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    ctx.strokeStyle = this.theme.colour;
    ctx.fillStyle = this.theme.colour;
    ctx.setLineDash(EMPTY_DASH);
    this.setFont(this.layout.fontPx);
  }

  setFont(sizePx: number): void {
    this.context.font = `${sizePx.toFixed(1)}px ${HUD.fontFamily}`;
  }

  /**
   * Stroke the current path, laying a dark outline underneath first when high-contrast mode is
   * on. The path survives `stroke()`, so both passes trace the same geometry.
   */
  stroke(widthScale = 1, colour?: string): void {
    const ctx = this.context;
    const width = this.layout.lineWidthPx * widthScale;
    if (this.theme.outline) {
      ctx.strokeStyle = this.theme.outlineColour;
      ctx.lineWidth = width + this.layout.lineWidthPx * this.theme.outlineWidthScale;
      ctx.stroke();
    }
    ctx.strokeStyle = colour ?? this.theme.colour;
    ctx.lineWidth = width;
    ctx.stroke();
  }

  line(x1: number, y1: number, x2: number, y2: number, widthScale = 1, colour?: string): void {
    const ctx = this.context;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    this.stroke(widthScale, colour);
  }

  dashedLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    dashPx: number,
    widthScale = 1,
    colour?: string,
  ): void {
    const ctx = this.context;
    dashPattern[0] = dashPx;
    dashPattern[1] = dashPx;
    ctx.setLineDash(dashPattern);
    this.line(x1, y1, x2, y2, widthScale, colour);
    ctx.setLineDash(EMPTY_DASH);
  }

  circle(x: number, y: number, radius: number, widthScale = 1, colour?: string): void {
    const ctx = this.context;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    this.stroke(widthScale, colour);
  }

  /**
   * Draw uppercase, letter-spaced text. Alignment is applied manually rather than through
   * `textAlign`, because the per-character fallback path has to know the run width anyway.
   */
  text(
    value: string,
    x: number,
    y: number,
    align: 'left' | 'centre' | 'right' = 'left',
    colour?: string,
  ): void {
    const ctx = this.context;
    const spacing = HUD.letterSpacingPx * this.layout.scale;
    const width = this.measure(value);

    let cursorX = x;
    if (align === 'centre') cursorX -= width * 0.5;
    else if (align === 'right') cursorX -= width;

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    if (this.letterSpacingSupported) {
      ctx.letterSpacing = `${spacing.toFixed(2)}px`;
      if (this.theme.outline) {
        ctx.lineWidth = this.layout.lineWidthPx * this.theme.outlineWidthScale;
        ctx.strokeStyle = this.theme.outlineColour;
        ctx.strokeText(value, cursorX, y);
      }
      ctx.fillStyle = colour ?? this.theme.colour;
      ctx.fillText(value, cursorX, y);
      ctx.letterSpacing = '0px';
      return;
    }

    for (let i = 0; i < value.length; i += 1) {
      const character = value[i];
      if (this.theme.outline) {
        ctx.lineWidth = this.layout.lineWidthPx * this.theme.outlineWidthScale;
        ctx.strokeStyle = this.theme.outlineColour;
        ctx.strokeText(character, cursorX, y);
      }
      ctx.fillStyle = colour ?? this.theme.colour;
      ctx.fillText(character, cursorX, y);
      cursorX += ctx.measureText(character).width + spacing;
    }
  }

  /** Width of a letter-spaced run, pixels. */
  measure(value: string): number {
    const spacing = HUD.letterSpacingPx * this.layout.scale;
    return this.context.measureText(value).width + spacing * Math.max(0, value.length - 1);
  }

  /**
   * A number formatted to `decimals`, cached per slot. Returns the previous string unchanged
   * when the value has not moved, so a steady readout allocates nothing.
   */
  number(slot: number, value: number, decimals = 0): string {
    const state = this.slots[slot];
    const rounded = roundTo(value, decimals);
    if (state.decimals === decimals && state.value === rounded) return state.text;
    state.value = rounded;
    state.decimals = decimals;
    state.text = rounded.toFixed(decimals);
    return state.text;
  }

  /** `M:SS` for endurance, cached on whole seconds. */
  duration(slot: number, seconds: number): string {
    const state = this.slots[slot];
    const whole = Math.max(0, Math.floor(seconds));
    if (state.value === whole && state.decimals === -2) return state.text;
    state.value = whole;
    state.decimals = -2;
    const minutes = Math.floor(whole / 60);
    const remainder = whole % 60;
    state.text = `${minutes}:${remainder < 10 ? '0' : ''}${remainder}`;
    return state.text;
  }
}

const EMPTY_DASH: number[] = [];
const dashPattern = [0, 0];

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
